
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { postData, fetchData } from "../redux/slices/apiCallSlice"; // Adjust the path as necessary
import { selectApiUrls } from "../redux/slices/apiUrlSlice"; // Adjust the path as necessary

const useCreateNewJob = () => {
  const [status, setStatus] = useState({});
  const dispatch = useDispatch();
  const apiUrls = useSelector(selectApiUrls);

  const postToApi = async (url, jobId, apiName, logs, apiStatuses, setApiStatus, additionalData = {}) => {
    apiStatuses[apiName] = "loading";
    setApiStatus({ ...apiStatuses });
    logs.push(`Patching to ${apiName} API...`);
    try {
      const response = await dispatch(postData({
        url,
        data: { projectID: String(jobId), ...additionalData },
        contract: { requestCase: 'pascal' },
      })).unwrap();
      setStatus((prevStatus) => ({ ...prevStatus, [apiName]: "success" }));
      logs.push(`Patched to ${apiName} API successfully.`);
      apiStatuses[apiName] = "success";
      return response; // Return the response to get the ID if needed
    } catch (error) {
      logs.push(`Failed to patch to ${apiName} API: ${error.message}`);
      apiStatuses[apiName] = "failed";
      return null;
    } finally {
      setApiStatus({ ...apiStatuses });
    }
  };

  // Helper function to handle parent-child relationships
  const postParentChildToApi = async (
    parentUrl, 
    childUrl, 
    jobId, 
    parentApiName, 
    childApiName, 
    logs, 
    apiStatuses, 
    setApiStatus, 
    parentAdditionalData = {}, 
    childLinkFieldName,
    childAdditionalData = {}
  ) => {
    // First create the parent record
    apiStatuses[parentApiName] = "loading";
    setApiStatus({ ...apiStatuses });
    logs.push(`Creating ${parentApiName} record (parent table)...`);
    
    try {
      // Post parent record
      const parentResponse = await dispatch(
        postData({ 
          url: parentUrl, 
          data: { 
            projectID: String(jobId), 
            ...parentAdditionalData 
          },
          contract: { requestCase: 'pascal' },
        })
      ).unwrap();
      
      setStatus((prevStatus) => ({ ...prevStatus, [parentApiName]: "success" }));
      logs.push(`Created ${parentApiName} record successfully.`);
      apiStatuses[parentApiName] = "success";
      setApiStatus({ ...apiStatuses });
      
      // Extract parent ID
      const parentId = parentResponse.ID || parentResponse.id;
      
      if (parentId) {
        // Now create the child record with parent ID reference
        apiStatuses[childApiName] = "loading";
        setApiStatus({ ...apiStatuses });
        logs.push(`Creating ${childApiName} record (child table)...`);
        
        // Post child record with parent ID reference
        const childData = { 
          projectID: String(jobId), 
          [childLinkFieldName]: parentId,
          ...childAdditionalData 
        };
        
        await dispatch(postData({
          url: childUrl,
          data: childData,
          contract: { requestCase: 'pascal' },
        })).unwrap();
        
        setStatus((prevStatus) => ({ ...prevStatus, [childApiName]: "success" }));
        logs.push(`Created ${childApiName} record successfully.`);
        apiStatuses[childApiName] = "success";
        setApiStatus({ ...apiStatuses });
        return true;
      } else {
        logs.push(`Failed to create ${childApiName} record: No parent ID available.`);
        apiStatuses[childApiName] = "failed";
        setApiStatus({ ...apiStatuses });
        return false;
      }
    } catch (error) {
      const failedApi = apiStatuses[parentApiName] === "loading" ? parentApiName : childApiName;
      logs.push(`Failed to create ${failedApi} record: ${error.message}`);
      apiStatuses[failedApi] = "failed";
      setApiStatus({ ...apiStatuses });
      return false;
    }
  };

  const createNewJob = async (jobData, setApiStatus, additionalData = {}) => {
    const logs = [];
    const apiStatuses = {
      generalProject: "loading",
      generalStation: "idle",
      soldToShipTo: "idle",
      priceBreakdown: "idle",
      ownersManual: "idle",
      hcRequirements: "idle",
      skidConstruction: "idle",
      pumpManifoldPiping: "idle",
      stationEnclosure: "idle",
      dropPipeComments: "idle",
      levelControls: "idle",
      pumps: "idle",
      pumpDetails: "idle",
      fertigation: "idle",
      skyHarvester: "idle",
      shipLoose: "idle",
      waterTreatment: "idle",
      inletScreen: "idle",
      inletStrainer: "idle",
      dischargeStrainer: "idle",
      dischargeFilter: "idle",
      roSystem: "idle",
      uvSystem: "idle",
      unregulatedPower: "idle",
      regulatedPowerSupply: "idle",
      sequenceOfOperations: "idle",
      electricalOptions: "idle",
      controlLogicOnly: "idle",
      telemetry: "idle",
      unregulatedBreakers: "idle",
      regulatedBreakers: "idle",
      telemetryAccessories: "idle",
    };

    try {
      // Post to generalProject API
      logs.push("Posting to generalProject API...");
      const generalProjectCreateResponse = await dispatch(
        postData({
          url: apiUrls.generalProject,
          data: jobData,
          contract: { requestCase: 'pascal' },
        })
      ).unwrap();
      setStatus((prevStatus) => ({ ...prevStatus, generalProject: "success" }));
      logs.push("Posted to generalProject API successfully.");
      apiStatuses.generalProject = "success";
      setApiStatus({ ...apiStatuses });

      let jobId = generalProjectCreateResponse?.ID || generalProjectCreateResponse?.id;

      if (!jobId) {
        logs.push("Fetching generalProject records to resolve the created job ID...");
        const generalProjectResponse = await dispatch(fetchData({ url: apiUrls.generalProject })).unwrap();
        const matchingRecord = generalProjectResponse.find(
          (record) => String(record.JobNumber || record.jobNumber || '').toLowerCase() === String(jobData.JobNumber || jobData.jobNumber || '').toLowerCase()
        );

        if (matchingRecord) {
          jobId = matchingRecord.ID || matchingRecord.id;
        }
      }

      if (!jobId) {
        throw new Error('Failed to resolve the created generalProject ID');
      }

      logs.push(`Resolved created generalProject ID: ${jobId}`);

      // Post to other APIs with projectId
      await postToApi(apiUrls.generalStation, jobId, "generalStation", logs, apiStatuses, setApiStatus, { zone: 1 });
      
      // Include additional soldToShipTo data if provided
      const soldToShipToData = { 
        zone: 1,
        ...(additionalData.soldToCompanyName && { soldToCompanyName: additionalData.soldToCompanyName }),
        ...(additionalData.soldToState && { soldToState: additionalData.soldToState })
      };
      await postToApi(apiUrls.soldToShipTo, jobId, "soldToShipTo", logs, apiStatuses, setApiStatus, soldToShipToData);
      
      // Include additional priceBreakdown data if provided
      const priceBreakdownData = {
        ...(additionalData.market && { market: additionalData.market }),
        ...(additionalData.sellPriceAmount && { sellPriceAmount: additionalData.sellPriceAmount })
      };
      await postToApi(apiUrls.priceBreakdown, jobId, "priceBreakdown", logs, apiStatuses, setApiStatus, priceBreakdownData);
      await postToApi(apiUrls.ownersManual, jobId, "ownersManual", logs, apiStatuses, setApiStatus);
      await postToApi(apiUrls.hcRequirements, jobId, "hcRequirements", logs, apiStatuses, setApiStatus, { zone: 1 });
      await postToApi(apiUrls.skidConstruction, jobId, "skidConstruction", logs, apiStatuses, setApiStatus, { zone: 1, hasSkid: false });
      await postToApi(apiUrls.pumpManifoldPiping, jobId, "pumpManifoldPiping", logs, apiStatuses, setApiStatus, { zone: 1, hasPiping: false });
      await postToApi(apiUrls.stationEnclosure, jobId, "stationEnclosure", logs, apiStatuses, setApiStatus, { zone: 1, hasEnclosure: false });
      await postToApi(apiUrls.dropPipeComments, jobId, "dropPipeComments", logs, apiStatuses, setApiStatus, { zone: 1, hasDropPipes: false });
      await postToApi(apiUrls.levelControls, jobId, "levelControls", logs, apiStatuses, setApiStatus, { zone: 1, hasLevelControls: false });
      const pumpsResponse = await postToApi(apiUrls.pumps, jobId, "pumps", logs, apiStatuses, setApiStatus, { zone: 1, hasSustain: false, hasMain: false, hasJockey: false, hasWaterFeature: false, hasTransfer: false, hasRecirc: false, hasTrash: false });
      const pumpParentId = pumpsResponse?.ID || pumpsResponse?.id;

      if (pumpParentId) {
        await postToApi(apiUrls.pumpDetails, jobId, "pumpDetails", logs, apiStatuses, setApiStatus, {
          zone: 1,
          pumpParentID: pumpParentId,
        });
      } else {
        logs.push("Skipped pumpDetails creation because no Pump Parent ID was returned from pumps API.");
        apiStatuses.pumpDetails = "skipped";
        setApiStatus({ ...apiStatuses });
      }

      await postToApi(apiUrls.skyHarvester, jobId, "skyHarvester", logs, apiStatuses, setApiStatus, { zone: 1, hasSky: false });
      await postToApi(apiUrls.shipLoose, jobId, "shipLoose", logs, apiStatuses, setApiStatus, { zone: 1, hasShipLoose: false });
      await postToApi(apiUrls.waterTreatment, jobId, "waterTreatment", logs, apiStatuses, setApiStatus, { hasInletFilter: false, hasInletStrainer: false, hasDischargeFilter: false, hasWyeStrainer: false, hasRO: false, hasUV: false, zone: 1, hasFilters: false });
      await postToApi(apiUrls.inletScreen, jobId, "inletScreen", logs, apiStatuses, setApiStatus, { zone: 1 });
      await postToApi(apiUrls.inletStrainer, jobId, "inletStrainer", logs, apiStatuses, setApiStatus, { zone: 1 });
      await postToApi(apiUrls.dischargeStrainer, jobId, "dischargeStrainer", logs, apiStatuses, setApiStatus, { zone: 1 });
      await postToApi(apiUrls.dischargeFilter, jobId, "dischargeFilter", logs, apiStatuses, setApiStatus, { zone: 1 });
      await postToApi(apiUrls.roSystem, jobId, "roSystem", logs, apiStatuses, setApiStatus, { zone: 1 });
      await postToApi(apiUrls.uvSystem, jobId, "uvSystem", logs, apiStatuses, setApiStatus, { zone: 1 });
      await postToApi(apiUrls.sequenceOfOperations, jobId, "sequenceOfOperations", logs, apiStatuses, setApiStatus, { zone: 1 });
      await postToApi(apiUrls.electricalOptions, jobId, "electricalOptions", logs, apiStatuses, setApiStatus, { zone: 1});
      await postToApi(apiUrls.fertigation, jobId, "fertigation", logs, apiStatuses, setApiStatus, { zone: 1, hasFertigation: false });
      await postToApi(apiUrls.controlLogicOnly, jobId, "controlLogicOnly", logs, apiStatuses, setApiStatus, { zone: 1, hasControlLogic: false });

      // Handle parent-child relationships
      // 1. Unregulated Power & Breakers
      await postParentChildToApi(
        apiUrls.unregulatedPower,
        apiUrls.unregulatedBreakers,
        jobId,
        "unregulatedPower",
        "unregulatedBreakers",
        logs,
        apiStatuses,
        setApiStatus,
        { zone: 1, hasUnregulated: false },
        "SupplyParentID"
      );

      // 2. Regulated Power & Breakers
      await postParentChildToApi(
        apiUrls.regulatedPower,
        apiUrls.regulatedBreakers,
        jobId,
        "regulatedPowerSupply",
        "regulatedBreakers",
        logs,
        apiStatuses,
        setApiStatus,
        { zone: 1, hasRegulated: false },
        "SupplyParentID"
      );

      // 3. Telemetry & Accessories
      await postParentChildToApi(
        apiUrls.telemetry,
        apiUrls.telemetryAccessories,
        jobId,
        "telemetry",
        "telemetryAccessories",
        logs,
        apiStatuses,
        setApiStatus,
        { zone: 1, hasTelemetry: false, hasAccessories: false },
        "TelemetryParentID"
      );

      return { jobId, logs, apiStatuses };
    } catch (error) {
      console.error("Error creating new job:", error);
      setStatus((prevStatus) => ({ ...prevStatus, [error.api]: "failed" }));
      logs.push(`Failed to create new job: ${error.message}`);
      apiStatuses[error.api] = "failed";
      setApiStatus({ ...apiStatuses });
      const createError = new Error(`Failed to create new job: ${error.message}`);
      createError.originalError = error;
      createError.logs = logs;
      createError.apiStatuses = apiStatuses;
      throw createError;
    }
  };

  return { createNewJob, status };
};

export default useCreateNewJob;
